"use client";
import { isFilled } from "@prismicio/client";
import Image from "next/image";

import React, { useState } from "react";
import { PrismicRichText, PrismicLink, PrismicImage } from "@prismicio/react";

export default function CFooter({ footer }) {
  const { text_newsletter, text_rgpd, text_yyp, title_colonne_1, links_1, title_colonne_2, links_2, title_colonne_3, social_media, app_links } = footer.data;

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !privacyAccepted || isLoading) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setEmail('');
        setPrivacyAccepted(false);
      } else {
        setMessage(data.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setMessage('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="w-full relative text-white">
      <div className="absolute -top-[42px] md:-top-[40px] xl:-top-[45px] w-full">
        <Image
          src="/yline-desktop-green.svg"
          alt=""
          className="hidden md:block w-full h-auto"
          width={1920}
          height={100}
          priority
          aria-hidden="true"
        />
        <Image
          src="/yline-mobile-green.svg"
          alt=""
          className="md:hidden w-full h-auto"
          width={600}
          height={100}
          priority
          aria-hidden="true"
        />
      </div>
      <div className="bg-green framing py-10">
        <div className="max-w-375 mx-auto w-full flex flex-col xl:flex-row gap-10 md:py-20 xl:gap-30 2xl:gap-40">
          <div className="flex flex-col gap-2.5">
            {isFilled.richText(text_newsletter) && (
              <PrismicRichText
                field={text_newsletter}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="title-newsletter">
                      {children}
                    </h2>
                  ),
                  paragraph: ({ children }) => (
                    <p className="">
                      {children}
                    </p>
                  ),
                }}
              />
            )}
          </div>
          <div>
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row flex-wrap gap-2.5 md:gap-4 ">
              <input
                type="email"
                name="email"
                placeholder="Ton adresse e-mail"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="w-full md:w-2/3 xl:w-full"
              />

              <label className="md:order-3 xl:order-2">
                <input
                  type="checkbox"
                  name="privacy"
                  required
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  disabled={isLoading}
                />
                {isFilled.richText(text_rgpd) && (
                  <PrismicRichText
                    field={text_rgpd}
                    components={{
                      paragraph: ({ children }) => (
                        <span className="p-sm">
                          {children}
                        </span>
                      ),
                    }}
                  />
                )}
              </label>
              <button
                type="submit"
                disabled={isLoading || !email || !privacyAccepted}
                className="cta-text mt-5 px-5 w-full md:mt-0 md:order-2 xl:order-3 md:flex-1 xl:flex-none xl:w-fit xl:px-20 xl:mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'EN COURS...' : 'VALIDER'}
              </button>
            </form>

            {message && (
              <div className={`mt-4 p-3 w-full rounded-lg text-sm ${message.includes('réussi') || message.includes('inscrit') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {message}
              </div>
            )}
          </div>
        </div>

      </div>
      <div className="bg-dark-green framing pt-10 pb-5 md:pb-6">
        <div className="flex flex-col md:flex-wrap md:flex-row gap-10 lg:justify-between">
          <div className="w-full lg:min-w-40 xl:w-60">
            {isFilled.richText(text_yyp) && (
              <PrismicRichText
                field={text_yyp}
                components={{
                  paragraph: ({ children }) => (
                    <p className="md:max-w-100">
                      {children}
                    </p>
                  ),
                }}
              />
            )}
          </div>

          <div className="flex flex-col gap-5 md:w-1/2 lg:w-auto lg:grow xl:flex-none">
            {isFilled.richText(title_colonne_1) && (
              <h3 className="h3-sm">{title_colonne_1}</h3>
            )}
            {Array.isArray(links_1) && links_1.length > 0 && (
              <div className="grid grid-cols-2 gap-y-4 gap-x-10 text-base">
                {links_1.map((link, idx) =>
                  isFilled.link(link) ? (
                    <div key={idx} className="">
                      <PrismicLink field={link} />
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-5 lg:grow xl:flex-none">
            {isFilled.richText(title_colonne_2) && (
              <h3 className="h3-sm">{title_colonne_2}</h3>
            )}
            {Array.isArray(links_2) && links_2.length > 0 && (
              <div className="flex flex-col gap-y-4 text-base">
                {links_2.map((link, idx) =>
                  isFilled.link(link) ? (
                    <div key={idx} className="">
                      <PrismicLink field={link} />
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-10 md:w-full md:flex-row md:items-center md:justify-between lg:flex-col lg:w-50 2xl:lg-w-60 lg:items-start">
            <div className="flex flex-col gap-2.5">
              {isFilled.richText(title_colonne_3) && (
                <h3 className="h3-sm text-center md:text-start">{title_colonne_3}</h3>
              )}
              {isFilled.group(social_media) && (
                <div className="flex gap-5 justify-center md:justify-start">
                  {social_media.map((item, idx) => (
                    <PrismicLink key={idx} className="" field={item.link}>
                      <PrismicImage
                        field={item.picto}
                        className="w-auto h-5 object-contain"
                      />
                    </PrismicLink>
                  ))}
                </div>
              )}
            </div>
            {Array.isArray(app_links) && app_links.length > 0 && (
              <div className="flex md:flex-wrap lg:flex-nowrap gap-5 max-w-100 mx-auto w-full">
                {app_links.map((link, idx) =>
                  isFilled.link(link) ? (
                    <div key={idx} className="w-1/2 md:w-auto w-1/2">
                      {link.variant == 'google_play' ?
                        <PrismicLink field={link}>
                          <img
                            src="/google-play.svg"
                            alt="Disponible sur Google play"
                            className="h-auto md:h-12 w-full"
                            width={200} // largeur explicite
                            height={60} // hauteur explicite
                          />
                        </PrismicLink> :
                        <PrismicLink field={link}>
                          <img
                            src="/app-store.svg"
                            alt="Télécharger dans l'App Store"
                            className="h-auto md:h-12 w-full"
                            width={200} // largeur explicite
                            height={60} // hauteur explicite
                          />
                        </PrismicLink>
                      }
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
          <div className="border-t-1 border-[#696963] flex flex-col pt-5 items-center gap-3 md:w-full md:flex-row md:justify-between md:pt-6">
            <svg width="144" height="25" viewBox="0 0 144 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M31.4273 0.552734L17.8971 24.233H24.8964L30.4958 14.5725L35.3687 24.233H42.368L33.9689 8.353L38.4798 0.552734H31.4273Z" fill="white" />
              <path d="M18.1173 0.552734L12.5178 10.2133L7.64493 0.552734H0.64563L9.04479 16.4327L4.53384 24.233H11.5837L25.1166 0.552734H18.1173Z" fill="#F1E146" />
              <path d="M65.5897 18.8201C65.3768 18.9186 65.1186 18.9665 64.8072 18.9665H57.3289V17.4176H64.8072C65.1186 17.4176 65.3768 17.4629 65.5897 17.556C65.7999 17.6492 65.9064 17.8594 65.9064 18.1894C65.9064 18.5194 65.7999 18.719 65.5897 18.8175M68.1499 15.0677C67.6442 14.7883 67.0534 14.5966 66.3774 14.4875C65.7015 14.3784 64.9776 14.3252 64.2031 14.3252H53.4567V24.2359H57.3289V21.681H64.2031C64.9776 21.681 65.7015 21.6438 66.3774 21.5719C67.0534 21.5001 67.6442 21.351 68.1499 21.1222C68.6555 20.8933 69.0521 20.55 69.3422 20.0842C69.6322 19.6185 69.776 18.9905 69.776 18.1947C69.776 17.399 69.6322 16.7496 69.3422 16.244C69.0521 15.7383 68.6555 15.3471 68.1499 15.0677Z" fill="white" />
              <path d="M78.0401 17.1495L80.1506 20.4096H75.9483L78.0428 17.1495H78.0401ZM75.8764 14.3232L69.2338 24.2339H73.4919L74.4553 22.7329H81.6542L82.6256 24.2339H86.8837L80.1798 14.3232H75.8738H75.8764Z" fill="white" />
              <path d="M102.34 20.0922C102.231 20.3238 102.056 20.518 101.813 20.6724C101.571 20.8267 101.249 20.9438 100.845 21.021C100.443 21.0982 99.9504 21.1381 99.3729 21.1381H92.2485V17.4229H99.3729C99.9504 17.4229 100.44 17.4602 100.845 17.54C101.246 17.6172 101.569 17.7343 101.813 17.8887C102.056 18.043 102.231 18.2373 102.34 18.4688C102.449 18.7004 102.503 18.9718 102.503 19.2832C102.503 19.5946 102.449 19.8634 102.34 20.0976M105.121 15.8262C104.605 15.3604 103.892 14.9958 102.984 14.7271C102.077 14.4583 100.924 14.3252 99.5326 14.3252H88.3843V24.2359H99.5326C100.853 24.2359 101.96 24.1028 102.854 23.834C103.748 23.5652 104.464 23.198 105.007 22.7349C105.55 22.2718 105.938 21.7422 106.175 21.1488C106.412 20.5553 106.532 19.9326 106.532 19.2832C106.532 18.6338 106.425 18.0111 106.215 17.4176C106.005 16.8242 105.64 16.2946 105.124 15.8315" fill="white" />
              <path d="M108.664 14.3232V24.2339H124.305V21.2932H112.536V20.053H122.136V18.5041H112.536V17.2666H124.273V14.3232H108.664Z" fill="white" />
              <path d="M126.575 14.3232V24.2339H139.474V21.2932H130.447V14.3232H126.575Z" fill="white" />
              <path d="M62.2473 6.31711V7.5626H73.0922V10.522H58.5533V0.552734H73.0922V3.5121H62.2473V4.76025H70.6065V6.31711H62.2473Z" fill="white" />
              <path d="M74.6114 6.87095H78.8642C78.9361 7.08917 79.149 7.2728 79.503 7.4245C79.8569 7.57619 80.3306 7.69063 80.9294 7.76781C81.5256 7.84498 82.2202 7.8849 83.0106 7.8849C83.801 7.8849 84.4051 7.86095 84.8575 7.81571C85.31 7.76781 85.6293 7.68531 85.8156 7.56555C86.0019 7.44579 86.0951 7.28345 86.0951 7.07587C86.0951 6.87893 85.9434 6.72458 85.6426 6.61546C85.3419 6.50635 84.807 6.43183 84.0379 6.38925C83.2687 6.34667 82.1882 6.32804 80.7964 6.32804C79.7584 6.32804 78.8616 6.27748 78.1084 6.17901C77.3553 6.08054 76.7378 5.92619 76.2535 5.71062C75.7718 5.49772 75.4151 5.22893 75.1863 4.90159C74.9574 4.57425 74.843 4.18304 74.843 3.72529C74.843 2.93755 75.1118 2.28553 75.644 1.76924C76.1789 1.25561 76.9907 0.869717 78.0738 0.616894C79.1596 0.361409 80.5169 0.236328 82.1483 0.236328C84.5355 0.236328 86.3612 0.582297 87.6253 1.27157C88.8868 1.96351 89.5175 2.95618 89.5175 4.25489H85.483C85.4297 3.91158 85.1077 3.65078 84.5169 3.46715C83.9234 3.28618 83.0824 3.19303 81.994 3.19303C80.9055 3.19303 80.1124 3.25424 79.5881 3.37932C79.0638 3.5044 78.8004 3.71731 78.8004 4.01803C78.8004 4.17505 78.8722 4.29747 79.0186 4.39328C79.165 4.48642 79.4151 4.56094 79.7744 4.61949C80.1337 4.67804 80.6393 4.7153 81.294 4.73659C81.9487 4.75788 82.7791 4.76852 83.7877 4.76852C84.8975 4.76852 85.8449 4.82175 86.622 4.93086C87.4018 5.03997 88.0325 5.20497 88.5142 5.42054C88.9959 5.63877 89.3472 5.91554 89.5654 6.25353C89.7837 6.59151 89.8928 6.98805 89.8928 7.44579C89.8928 8.2362 89.6453 8.88023 89.145 9.37789C88.6473 9.87556 87.8569 10.2455 86.7764 10.485C85.6959 10.7245 84.2827 10.8443 82.5395 10.8443C81.5522 10.8443 80.6473 10.7884 79.8196 10.6739C78.9946 10.5595 78.2601 10.3918 77.6161 10.1683C76.972 9.94475 76.4265 9.67064 75.9793 9.3433C75.5322 9.01596 75.1916 8.6487 74.9601 8.2362C74.7259 7.82636 74.6088 7.37127 74.6088 6.87361" fill="white" />
              <path d="M53.0308 0.552734L49.6855 5.0663L46.3428 0.552734H42.0448L47.7001 7.98308V10.522H51.6708V7.98308L57.3288 0.552734H53.0308Z" fill="white" />
              <path d="M115.709 6.31711V7.5626H126.554V10.522H112.015V0.552734H126.554V3.5121H115.709V4.76025H124.068V6.31711H115.709Z" fill="white" />
              <path d="M128.07 6.87095H132.323C132.395 7.08917 132.608 7.2728 132.962 7.4245C133.316 7.57619 133.79 7.69063 134.388 7.76781C134.984 7.84498 135.679 7.8849 136.469 7.8849C137.26 7.8849 137.864 7.86095 138.316 7.81571C138.769 7.76781 139.088 7.68531 139.275 7.56555C139.461 7.44579 139.554 7.28345 139.554 7.07587C139.554 6.87893 139.405 6.72458 139.102 6.61546C138.801 6.50635 138.266 6.43183 137.497 6.38925C136.728 6.34667 135.647 6.32804 134.255 6.32804C133.217 6.32804 132.32 6.27748 131.567 6.17901C130.814 6.08054 130.197 5.92619 129.715 5.71062C129.233 5.49772 128.877 5.22893 128.648 4.90159C128.419 4.57425 128.305 4.18304 128.305 3.72529C128.305 2.93755 128.573 2.28553 129.106 1.76924C129.64 1.25561 130.452 0.869717 131.535 0.616894C132.621 0.361409 133.978 0.236328 135.61 0.236328C137.997 0.236328 139.823 0.582297 141.087 1.27157C142.348 1.96351 142.979 2.95618 142.979 4.25489H138.945C138.891 3.91158 138.569 3.65078 137.978 3.46715C137.385 3.28618 136.544 3.19303 135.456 3.19303C134.367 3.19303 133.574 3.25424 133.05 3.37932C132.525 3.5044 132.262 3.71731 132.262 4.01803C132.262 4.17505 132.334 4.29747 132.48 4.39328C132.624 4.48642 132.877 4.56094 133.236 4.61949C133.595 4.67804 134.101 4.7153 134.756 4.73659C135.41 4.75788 136.241 4.76852 137.249 4.76852C138.359 4.76852 139.306 4.82175 140.084 4.93086C140.863 5.03997 141.494 5.20497 141.976 5.42054C142.457 5.63877 142.809 5.91554 143.027 6.25353C143.245 6.59151 143.354 6.98805 143.354 7.44579C143.354 8.2362 143.107 8.88023 142.607 9.37789C142.109 9.87556 141.318 10.2455 140.238 10.485C139.157 10.7245 137.744 10.8443 136.001 10.8443C135.014 10.8443 134.109 10.7884 133.281 10.6739C132.456 10.5595 131.722 10.3918 131.078 10.1683C130.434 9.94475 129.888 9.67064 129.441 9.3433C128.994 9.01596 128.653 8.6487 128.422 8.2362C128.187 7.82636 128.07 7.37127 128.07 6.87361" fill="white" />
              <path d="M106.492 0.552734L103.147 5.0663L99.8019 0.552734H95.5065L101.162 7.98308V10.522H105.133V7.98308L110.788 0.552734H106.492Z" fill="white" />
            </svg>
            <p className="p-xxs text-center">
              © 2026 Yes Yes Padel. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
